const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro';

interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
  /** Set by guards / services when the failure is structured (e.g.
   *  MODULE_NOT_SUBSCRIBED, SMTP_NOT_CONFIGURED). */
  errorCode?: string;
  /** Module codes the endpoint requires (paired with errorCode). */
  requiredModules?: string[];
  /** Alternate message for admins — used when the default message points at
   *  an action only admins can take. Currently set on SMTP_NOT_CONFIGURED. */
  adminMessage?: string;
}

/** Pages where we should NOT hard-redirect on 401.
 *  /accept-invitation is included so a re-invited user with stale cookies
 *  (deactivated/zombie account) doesn't get bounced to /login the moment
 *  auth-store.initialize() fires its first /auth/profile call. */
const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/accept-invitation'];

/** Endpoints where a 401 IS the real answer (no token to refresh).
 *  We must NOT mask the backend message with a generic "Sesiune expirată". */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password', '/auth/verify-email', '/auth/resend-verification'];

function isAuthEndpoint(endpoint: string): boolean {
  return AUTH_ENDPOINTS.some((p) => endpoint.startsWith(p));
}

function isOnAuthPage(): boolean {
  if (typeof window === 'undefined') return false;
  return AUTH_PAGES.some((page) => window.location.pathname.startsWith(page));
}

class ApiClient {
  /**
   * Single-flight refresh: shared promise across concurrent tryRefresh() calls.
   * The backend blacklists the refresh token on rotation, so parallel refresh
   * calls would race and all-but-one would get 401. By deduplicating them here,
   * only one POST /auth/refresh is in flight at a time and all callers wait
   * for the same result.
   */
  private refreshPromise: Promise<boolean> | null = null;

  /**
   * Hardening multi-tab (2026-06-11): după fiecare refresh reușit, identitatea
   * din răspuns (user/tenant) e raportată aici. auth-store înregistrează
   * callback-ul la bootstrap și forțează resync/reload dacă sesiunea din
   * cookie nu mai corespunde identității afișate în UI (race-ul «tabul vechi
   * rescrie cookie-urile peste login-ul nou»). Callback injectat (nu import)
   * ca să nu creăm dependență shared → features.
   */
  onRefreshIdentity: ((identity: { userId: string | null; tenantId: string | null }) => void) | null = null;

  async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_URL}/api/v1${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    // Detect read-only (GET) vs deliberate action (POST/PATCH/DELETE/PUT).
    // Pentru fetch-uri silente (GET la load) NU afișez toast 402 — userul
    // n-a făcut nimic deliberat. Pentru acțiuni deliberate (click, submit),
    // afișez toast cu mesajul de upgrade.
    const httpMethod = ((options.method as string | undefined) ?? 'GET').toUpperCase();
    const isReadOnlyRequest = httpMethod === 'GET' || httpMethod === 'HEAD';

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      // For auth endpoints (login/register/refresh/etc.), 401 IS the answer.
      // Don't attempt token refresh and don't mask with "Sesiune expirată" —
      // propagate the backend's real message ("Email sau parolă incorectă",
      // "Contul este dezactivat", etc.) so the user sees what's wrong.
      if (isAuthEndpoint(endpoint)) {
        const error = await response.json().catch(() => ({
          statusCode: 401,
          message: 'Autentificare eșuată',
        }));
        throw error as ApiError;
      }

      // Read the original 401 body upfront so we can fall back to its
      // real message if the refresh-token retry fails.
      const originalError = await response.clone().json().catch(() => null);

      // Try to refresh token (deduplicated across concurrent callers)
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        const retryResponse = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });
        if (!retryResponse.ok) {
          const error = await retryResponse.json().catch(() => ({
            statusCode: retryResponse.status,
            message: 'Eroare de server',
          }));
          throw error as ApiError;
        }
        return retryResponse.json();
      } else {
        // Only redirect to /login if NOT already on an auth page
        // This prevents the infinite reload loop
        if (typeof window !== 'undefined' && !isOnAuthPage()) {
          window.location.href = '/login';
        }
        throw (originalError ?? { statusCode: 401, message: 'Sesiune expirată' }) as ApiError;
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        statusCode: response.status,
        message: 'Eroare de server',
      }));
      // Subscription gate (ModuleGuard) — surface a single, consistent toast
      // so callers don't each have to format their own message. Component
      // can still inspect `errorCode === 'MODULE_NOT_SUBSCRIBED'` and offer
      // an "upgrade plan" CTA if the surrounding context warrants it.
      if (response.status === 403 && (error as ApiError).error === 'CONSENT_REACCEPTANCE_REQUIRED') {
        // Termenii au fost actualizați — redirect la pagina de re-acceptare.
        // Evităm redirect dacă suntem deja pe /re-acceptance (loop) sau pe pagini auth.
        if (typeof window !== 'undefined') {
          const path = window.location.pathname;
          if (!path.startsWith('/re-acceptance') && !isOnAuthPage()) {
            window.location.href = '/re-acceptance';
          }
        }
        throw error as ApiError;
      }

      if (response.status === 402 && (error as ApiError).errorCode === 'MODULE_NOT_SUBSCRIBED' && !isReadOnlyRequest) {
        // Skip toast pentru GET (fetch silent la load) — user n-a făcut nimic deliberat.
        // Afișez doar pentru POST/PATCH/DELETE (click pe buton, submit form).
        try {
          const { toast } = await import('sonner');
          // Deduplicare: folosesc id derivat din modulele cerute astfel încât
          // toast-urile pentru același modul se înlocuiesc, nu se duplică
          // (când 2 componente fac fetch paralel pe endpoint-uri ale aceluiași modul).
          const required = ((error as ApiError & { requiredModules?: string[] }).requiredModules || []).join('-');
          const toastId = `module-not-subscribed-${required || 'unknown'}`;
          toast.error((error as ApiError).message ?? 'Modulul nu este activ în abonament.', {
            id: toastId,
            action: {
              label: 'Vezi planurile',
              onClick: () => {
                if (typeof window !== 'undefined') {
                  window.location.href = '/preturi';
                }
              },
            },
            duration: 7000,
          });
        } catch {}
      }
      // Paid plan gate (PlanGuard) — free_permanent încearcă să acceseze
      // features paid-only (e-Factura, SAF-T, bank, multi-firmă). Show toast
      // cu link spre upgrade.
      if (response.status === 402 && (error as ApiError).errorCode === 'PAID_PLAN_REQUIRED' && !isReadOnlyRequest) {
        try {
          const { toast } = await import('sonner');
          toast.error((error as ApiError).message ?? 'Această funcționalitate necesită un plan plătit.', {
            action: { label: 'Vezi planurile', onClick: () => { if (typeof window !== 'undefined') window.location.href = '/setari?tab=billing'; } },
            duration: 8000,
          });
        } catch {}
      }
      // Monthly limit reached (free_permanent: 10 facturi/lună hard block) —
      // surfacing toast cu CTA upgrade.
      if (response.status === 402 && (error as ApiError).errorCode === 'MONTHLY_LIMIT_REACHED') {
        try {
          const { toast } = await import('sonner');
          toast.error((error as ApiError).message ?? 'Limita lunară atinsă. Activează planul plătit pentru nelimitat.', {
            action: { label: 'Vezi planurile', onClick: () => { if (typeof window !== 'undefined') window.location.href = '/setari?tab=billing'; } },
            duration: 8000,
          });
        } catch {}
      }
      // SMTP not configured — backend ships two messages: `message` (for
      // non-admin: "contact admin") and `adminMessage` (for admins: "go to
      // Settings → Email"). We pick based on the caller's permissions so
      // the toast points at an action they can actually take. Done here
      // (not in each caller) so the UX is consistent everywhere email is
      // triggered (invoice, reminder, receipt, etc.).
      if (response.status === 400 && (error as ApiError).errorCode === 'SMTP_NOT_CONFIGURED') {
        try {
          const { toast } = await import('sonner');
          const { useAuthStore } = await import('@/features/auth/stores/auth-store');
          const perms = useAuthStore.getState().user?.permissions ?? [];
          const isAdmin = perms.includes('settings.company');
          const msg = isAdmin
            ? ((error as ApiError & { adminMessage?: string }).adminMessage ?? (error as ApiError).message)
            : (error as ApiError).message;
          toast.error(msg ?? 'Email-ul firmei nu este configurat.');
        } catch {}
      }
      throw error as ApiError;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'DELETE' });
  }

  /** Upload multipart (FormData) — NU seta Content-Type (browserul pune boundary-ul). */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${API_URL}/api/v1${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        statusCode: response.status,
        message: 'Eroare la încărcarea fișierului',
      }));
      throw error as ApiError;
    }
    if (response.status === 204) return {} as T;
    return response.json();
  }

  private async tryRefresh(): Promise<boolean> {
    // If a refresh is already in flight, wait for it instead of starting a new one.
    // This prevents the race where multiple parallel 401 responses each trigger
    // their own POST /auth/refresh and blacklist each other's tokens.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          credentials: 'include',
        });
        if (response.ok && this.onRefreshIdentity) {
          // Backend-ul returnează deja { user, tenant } la refresh — îl folosim
          // pentru reconcilierea identității (vezi comentariul de la hook).
          try {
            const body = await response.clone().json().catch(() => null) as
              { user?: { id?: string }; tenant?: { id?: string } } | null;
            if (body?.user?.id) {
              this.onRefreshIdentity({
                userId: body.user.id,
                tenantId: body.tenant?.id ?? null,
              });
            }
          } catch { /* reconcilierea e best-effort — refresh-ul rămâne valid */ }
        }
        return response.ok;
      } catch {
        return false;
      }
    })();

    try {
      return await this.refreshPromise;
    } finally {
      // Clear shortly after so a later genuine refresh can run.
      // Small delay lets all concurrent callers observe the same result
      // and benefit from the freshly rotated cookies on their retries.
      setTimeout(() => { this.refreshPromise = null; }, 100);
    }
  }

  /**
   * Public single-flight session refresh. Folosit de call-site-urile cu `fetch` BRUT
   * (download/preview PDF/blob) care NU trec prin `ApiClient.fetch` și deci nu beneficiază
   * de logica internă 401 -> refresh -> retry. Returnează `true` dacă refresh-ul a reușit
   * (cookie-urile rotite sunt deja setate, request-ul brut poate fi reîncercat o dată).
   * Se coalesce cu orice refresh deja în curs (dedup prin `refreshPromise`).
   */
  async refreshSession(): Promise<boolean> {
    return this.tryRefresh();
  }
}

export const api = new ApiClient();
export type { ApiError };
